import { useEffect, useState } from 'react';
import type React from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams, useNavigate } from '@change-corgi/core/react/router';

import { useEndorsements } from 'src/app/shared/components/endorsements';
import type { EndorsementFormData } from 'src/app/shared/components/endorsements';
import { ImageUploadProvider, useImageUploadContext } from 'src/app/shared/hooks/imageUpload';
import { useToast } from 'src/app/shared/toast';

import { useEditEndorsement } from '../../hooks/useEditEndorsement';
import type { EndorsementFormSchema } from '../../shared/validation';
import { EndorsementForm } from '../EndorsementForm';

import { contentContainer, formSection, pageContainer, previewSection } from './EditEndorsement.styles';

function prepareInitialData(endorsement?: {
	name: string;
	title?: string | null;
	url?: string | null;
	content?: string | null;
	photo?: { userSmall: { url: string } } | null;
}) {
	if (!endorsement) return undefined;

	return {
		name: endorsement.name,
		title: endorsement.title || '',
		url: endorsement.url || '',
		statement: endorsement.content || '',
		photoUrl: endorsement.photo?.userSmall.url,
	};
}

function createHandleSubmit(actions: {
	handleFieldChange: (field: keyof EndorsementFormData, value: string) => void;
	handlePhotoUpload: (url: string | undefined) => void;
	handleNext: () => void;
}) {
	return (formData: EndorsementFormSchema) => {
		// React Hook Form manages the form state, so we just need to update
		// the hook's internal state for submission
		actions.handleFieldChange('name', formData.name || '');
		actions.handleFieldChange('title', formData.title || '');
		actions.handleFieldChange('statement', formData.statement || '');
		actions.handleFieldChange('url', formData.url || '');
		if (formData.photoUrl) {
			actions.handlePhotoUpload(formData.photoUrl);
		}

		// Trigger next step
		actions.handleNext();
	};
}

// eslint-disable-next-line max-statements
const EditEndorsementContent: React.FC = () => {
	const { slug, endorsementId } = useKnownAvailableParams<{ slug: string; endorsementId: string }>();
	const { translate } = useI18n();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
	const { dispatch: imageUploadDispatch } = useImageUploadContext();

	const {
		data: { endorsements, isLoading: isLoadingEndorsements },
	} = useEndorsements({ petitionSlugOrId: slug });

	const endorsement = endorsements.find((e) => e.id === endorsementId);
	const initialData = prepareInitialData(endorsement);

	const { data, actions, uploader } = useEditEndorsement({
		endorsementId,
		initialData,
		onSuccess: () => {
			navigate(`/p/${slug}/edit?endorsementUpdated=true#endorsements`);
		},
	});

	// Initialize image upload context with existing photo
	useEffect(() => {
		if (endorsement?.photo?.userMedium.url && !data.image) {
			imageUploadDispatch({
				type: 'SET_IMAGE',
				payload: { image: endorsement.photo.userMedium.url, uploader },
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [endorsement?.id]);

	// Show error toast when error occurs
	useEffect(() => {
		if (data.error) {
			showToast({
				variant: 'error',
				message:
					data.error.type === 'VALIDATION'
						? translate('corgi.petition.starter_dashboard_v2.endorsements.add.form_validation_error')
						: data.error.message,
			});
			actions.clearError();
		}
	}, [data.error, showToast, actions, translate]);

	const handleBack = () => {
		navigate(`/p/${slug}/edit#endorsements`);
	};

	const handleSubmit = createHandleSubmit(actions);

	const handleInvalidSubmit = () => {
		showToast({
			variant: 'error',
			message: translate('corgi.petition.starter_dashboard_v2.endorsements.add.form_validation_error'),
		});
	};

	const handleConfirmSubmit = async () => {
		if (!data.isTermsAgreed) {
			showToast({
				variant: 'error',
				message: translate('corgi.petition.starter_dashboard_v2.endorsements.add.terms_required_error'),
				bottomPosition: 'bottom-[30vh]',
			});
			return;
		}
		await actions.handleSubmit();
	};

	// Don't render form until endorsement data is loaded
	if (isLoadingEndorsements || !endorsement || !initialData) {
		return null;
	}

	return (
		<EndorsementForm
			key={endorsementId}
			data-qa="edit-endorsement-form"
			title={translate('corgi.petition.starter_dashboard_v2.endorsements.edit.page_title')}
			backButtonAriaLabel={translate('corgi.petition.starter_dashboard_v2.endorsements.edit.back_button_aria_label')}
			defaultValues={initialData}
			isSubmitting={data.isSubmitting}
			isHelpModalOpen={isHelpModalOpen}
			isSubmitModalOpen={data.isSubmitModalOpen}
			onBack={handleBack}
			onSubmit={handleSubmit}
			onInvalidSubmit={handleInvalidSubmit}
			onHelpClick={() => setIsHelpModalOpen(true)}
			onCloseHelpModal={() => setIsHelpModalOpen(false)}
			onCloseSubmitModal={() => actions.setIsSubmitModalOpen(false)}
			onConfirmSubmit={handleConfirmSubmit}
			pageContainer={pageContainer()}
			contentContainer={contentContainer()}
			formSection={formSection()}
			previewSection={previewSection()}
			testId="edit-endorsement-page"
			uppy={uploader.uppy}
			image={data.image ?? data.formData.photoUrl}
			uploadErrorMessage={data.imageUploadErrorMessage}
			isTermsAgreed={data.isTermsAgreed}
			onTermsChange={actions.handleTermsChange}
		/>
	);
};

export const EditEndorsement: React.FC = () => {
	return (
		<ImageUploadProvider>
			<EditEndorsementContent />
		</ImageUploadProvider>
	);
};
